/*
 * @Author: Yumeng Xue (w/ Gemini)
 * @Description: 
 * 热图生成工具 (增强版)。
 * 新增：直方图均衡化 (Histogram Equalization) 支持，解决数据长尾分布导致的颜色挤压问题。
 * @FilePath: /topo-mess/src/utils/heatmap.ts
 */
import { TdaScanResult } from "../core/tda-scanner";
import { StructureTensorScanResult } from "../core/structure-tensor-scanner";
import { interpolateMagma } from "d3-scale-chromatic";
import * as d3 from 'd3'; // 引入 d3 用于快速二分查找
import { getColormapColor, ColormapName } from "../core/colormaps";

// -----------------------------------------------------------------------------
// 1. HeatmapMode 类型定义
// -----------------------------------------------------------------------------
export type HeatmapMode = "Saliency" | "Density" | "Messiness" | "IlluminatedMessiness" | "Off";

// 支持两种扫描结果类型
type ScanResult = TdaScanResult | StructureTensorScanResult;

export interface HeatmapNormalizationOptions {
    densityReference?: ScanResult | null;
}

// --- 配置常量 ---
const LOG_SCALE_DENSITY = false;        // (推荐) D(p) 密度通常呈指数分布，保持开启
const USE_HISTOGRAM_EQUALIZATION = true; // (新增) 开启直方图均衡化，解决 M(p) 都在 1 附近看不清的问题

// --- 光照配置常量 ---
const SHADING_MAGNITUDE = -65;           // φ 参数（负数），控制阴影强度
const LIGHT_THETA = Math.PI / 3;         // 60° 俯角
const LIGHT_PHI = (5 * Math.PI) / 4;     // 225° 方位角（左上方）


// -----------------------------------------------------------------------------
// 2. 颜色映射函数
// -----------------------------------------------------------------------------

// 当前使用的 colormap（模块级变量）
let currentColormap: ColormapName = 'magma';
let currentReversed: boolean = true;

/**
 * 设置当前使用的 colormap
 */
export function setColormap(colormap: ColormapName, reversed: boolean = true): void {
    currentColormap = colormap;
    currentReversed = reversed;
}

/**
 * 获取当前使用的 colormap
 */
export function getCurrentColormap(): { colormap: ColormapName; reversed: boolean } {
    return { colormap: currentColormap, reversed: currentReversed };
}

/**
 * 使用动态 colormap 映射归一化值到颜色
 * @param t 归一化值 [0, 1]
 * @param colormap 可选，指定 colormap，不传则使用当前设置
 * @param reversed 可选，是否反转，不传则使用当前设置
 */
function getHeatmapColorValue(t: number, colormap?: ColormapName, reversed?: boolean): [number, number, number] {
    const clamped = Math.max(0, Math.min(1, t));
    const useColormap = colormap ?? currentColormap;
    const useReversed = reversed ?? currentReversed;

    return getColormapColor(clamped, useColormap, useReversed);
}

/**
 * 使用 d3 Magma colormap 映射归一化值到颜色
 * 反转映射：浅色代表低值，深色代表高值
 * @deprecated 使用 getHeatmapColorValue 代替
 */
function getMagmaColor(t: number): [number, number, number] {
    return getHeatmapColorValue(t);
}

// -----------------------------------------------------------------------------
// RGB <-> LAB 颜色空间转换
// -----------------------------------------------------------------------------
function srgbToLinear(c: number): number {
    const v = c / 255.0;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
function linearToSrgb(c: number): number {
    const v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(1, v)) * 255);
}
function rgbToXyz(r: number, g: number, b: number): [number, number, number] {
    const rl = srgbToLinear(r); const gl = srgbToLinear(g); const bl = srgbToLinear(b);
    const x = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375;
    const y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750;
    const z = rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041;
    return [x * 100, y * 100, z * 100];
}
function xyzToLab(x: number, y: number, z: number): [number, number, number] {
    const xn = 95.047; const yn = 100.000; const zn = 108.883;
    const fx = labF(x / xn); const fy = labF(y / yn); const fz = labF(z / zn);
    const L = 116 * fy - 16; const a = 500 * (fx - fy); const b = 200 * (fy - fz);
    return [L, a, b];
}
function labF(t: number): number {
    const delta = 6 / 29;
    return t > delta ** 3 ? Math.pow(t, 1 / 3) : t / (3 * delta ** 2) + 4 / 29;
}
function rgbToLab(r: number, g: number, b: number): [number, number, number] {
    const [x, y, z] = rgbToXyz(r, g, b); return xyzToLab(x, y, z);
}
function labToXyz(L: number, a: number, b: number): [number, number, number] {
    const xn = 95.047; const yn = 100.000; const zn = 108.883;
    const fy = (L + 16) / 116; const fx = a / 500 + fy; const fz = fy - b / 200;
    return [xn * labFInv(fx), yn * labFInv(fy), zn * labFInv(fz)];
}
function labFInv(t: number): number {
    const delta = 6 / 29; return t > delta ? t ** 3 : 3 * delta ** 2 * (t - 4 / 29);
}
function xyzToRgb(x: number, y: number, z: number): [number, number, number] {
    x /= 100; y /= 100; z /= 100;
    const rl = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
    const gl = x * -0.9692660 + y * 1.8760108 + z * 0.0415560;
    const bl = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;
    return [linearToSrgb(rl), linearToSrgb(gl), linearToSrgb(bl)];
}
function labToRgb(L: number, a: number, b: number): [number, number, number] {
    const [x, y, z] = labToXyz(L, a, b); return xyzToRgb(x, y, z);
}

// -----------------------------------------------------------------------------
// 3. 核心颜色逻辑
// -----------------------------------------------------------------------------

function getHeatmapColor(
    m_norm: number,
    d_norm: number,
    s_norm: number,
    mode: HeatmapMode
): [number, number, number, number] {

    let r = 0, g = 0, b = 0, a = 0;

    switch (mode) {
        case "Saliency":
            // 颜色=M(S_norm), 透明度=D
            [r, g, b] = getMagmaColor(s_norm);
            a = (d_norm === 0) ? 0 : 255;
            break;

        case "Density":
            // 颜色=D, 透明度=100%
            [r, g, b] = getMagmaColor(d_norm);
            a = (d_norm === 0) ? 0 : 255;
            break;

        case "Messiness":
            // 颜色=M, 透明度=100% (过滤低密度区)
            [r, g, b] = getMagmaColor(m_norm);
            // 这里我们用 d_norm > 0 来过滤背景，保证背景是透明的
            a = (d_norm === 0) ? 0 : 255;
            break;

        case "Off":
        default:
            r = 0; g = 0; b = 0; a = 0;
            break;
    }

    return [r, g, b, a];
}


// -----------------------------------------------------------------------------
// 4. (新增) 辅助函数：分位数查找
// -----------------------------------------------------------------------------

/**
 * 给定一个数值 val 和一个已排序的数组 sortedArr，
 * 返回 val 在数组中的排名百分比 [0, 1]。
 * 相当于 Cumulative Distribution Function (CDF)。
 */
function getRankQuantile(val: number, sortedArr: Float32Array): number {
    if (sortedArr.length === 0) return 0;
    // d3.bisectRight 返回插入点索引，相当于有多少个元素 <= val
    const idx = d3.bisectRight(sortedArr, val);
    return idx / sortedArr.length;
}

function getDensityScoreMatrix(scanResult: ScanResult): number[][] {
    return 'densityScores' in scanResult ? scanResult.densityScores : scanResult.dScores;
}

function computeDensityMinMax(
    densityScores: number[][],
    width: number,
    height: number
): { min: number; max: number } {
    let min = Infinity;
    let max = -Infinity;

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const d = densityScores[x]?.[y] || 0;
            if (d > 0) {
                min = Math.min(min, d);
                max = Math.max(max, d);
            }
        }
    }

    if (!isFinite(min) || !isFinite(max)) {
        return { min: 0, max: 1 };
    }

    return { min, max };
}

function computeDensityScaleStats(
    densityScores: number[][],
    width: number,
    height: number
): { max: number; maxLog: number } {
    let max = 0;
    let maxLog = 0;

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const d = densityScores[x]?.[y] || 0;
            if (d > max) max = d;
            const logD = Math.log1p(d);
            if (logD > maxLog) maxLog = logD;
        }
    }

    return { max, maxLog };
}

function normalizeToUnitInterval(
    value: number,
    min: number,
    max: number,
    fallback: number = 0
): number {
    if (!isFinite(min) || !isFinite(max)) {
        return fallback;
    }

    const range = max - min;
    if (range <= 1e-10) {
        return fallback;
    }

    const normalized = (value - min) / range;
    return Math.max(0, Math.min(1, normalized));
}

// -----------------------------------------------------------------------------
// 5. 主函数：生成像素
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// 光照辅助函数
// -----------------------------------------------------------------------------

/**
 * 使用 Sobel 算子计算梯度（优化版本）
 */
function computeSobelGradient(
    matrix: number[][],
    x: number,
    y: number
): { gx: number; gy: number } {
    const width = matrix.length;
    const height = matrix[0].length;

    // 边界处理：镜像填充
    // 注意：matrix 是 [x][y] 格式
    // 优化：直接内联边界检查，避免函数调用
    const x0 = Math.max(0, x - 1);
    const x1 = x;
    const x2 = Math.min(width - 1, x + 1);
    const y0 = Math.max(0, y - 1);
    const y1 = y;
    const y2 = Math.min(height - 1, y + 1);

    // Sobel 核 - 直接计算，避免循环
    const gx =
        -matrix[x0][y0] + matrix[x2][y0] +
        -2 * matrix[x0][y1] + 2 * matrix[x2][y1] +
        -matrix[x0][y2] + matrix[x2][y2];

    const gy =
        -matrix[x0][y0] - 2 * matrix[x1][y0] - matrix[x2][y0] +
        matrix[x0][y2] + 2 * matrix[x1][y2] + matrix[x2][y2];

    return { gx, gy };
}

/** 3D 向量归一化 */
function normalize3D(x: number, y: number, z: number): [number, number, number] {
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len < 1e-10) return [0, 0, 1]; // 避免除零
    return [x / len, y / len, z / len];
}

/** 3D 向量点积 */
function dot3D(
    a: [number, number, number],
    b: [number, number, number]
): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * 生成光照 Messiness 热图 (CPU 版本)
 */
function createIlluminatedMessinessHeatmap(
    scanResult: ScanResult,
    width: number,
    height: number,
    shadingMagnitude: number = SHADING_MAGNITUDE,
    lightTheta: number = LIGHT_THETA,
    lightPhi: number = LIGHT_PHI,
    normalizationOptions: HeatmapNormalizationOptions = {}
): Uint8Array {
    const startTime = performance.now();

    // 1. 提取数据
    const mScores = 'coherenceScores' in scanResult
        ? scanResult.coherenceScores
        : scanResult.mScores;
    const dScores = 'densityScores' in scanResult
        ? scanResult.densityScores
        : scanResult.dScores;

    if (!mScores || !dScores) {
        console.error('Invalid scan result: missing mScores or dScores');
        return new Uint8Array(width * height * 4);
    }

    // 2. 计算光源向量（预计算）
    const sinTheta = Math.sin(lightTheta);
    const cosTheta = Math.cos(lightTheta);
    const sinPhi = Math.sin(lightPhi);
    const cosPhi = Math.cos(lightPhi);

    const lx = sinTheta * cosPhi;
    const ly = sinTheta * sinPhi;
    const lz = cosTheta;
    const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);

    const lightDirX = lx / lLen;
    const lightDirY = ly / lLen;
    const lightDirZ = lz / lLen;
    const I_empty = Math.max(0, lightDirZ);

    // 3. 第一步：归一化 Density 矩阵（用于梯度计算）
    const densityReference = normalizationOptions.densityReference ?? scanResult;
    const { min: d_min, max: d_max } = computeDensityMinMax(
        getDensityScoreMatrix(densityReference),
        width,
        height
    );
    const d_range = d_max - d_min + 1e-10;

    // 创建归一化的 density 矩阵
    const dScoresNormalized: number[][] = [];
    for (let x = 0; x < width; x++) {
        const col: number[] = [];
        for (let y = 0; y < height; y++) {
            const d = dScores[x][y];
            col.push(d > 0 ? (d - d_min) / d_range : 0);
        }
        dScoresNormalized.push(col);
    }

    // 4. 第二步：直方图均衡化 Messiness 值
    const validMValues: number[] = [];
    let maxM = 0;

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const d = dScores[x][y];
            const m = mScores[x][y];
            if (d > 0 && m > 0) {
                validMValues.push(m);
                if (m > maxM) maxM = m;
            }
        }
    }

    // 构建 CDF 用于直方图均衡化
    let sortedM: Float32Array;
    if (USE_HISTOGRAM_EQUALIZATION && validMValues.length > 0) {
        sortedM = Float32Array.from(validMValues.sort((a, b) => a - b));
    } else {
        sortedM = new Float32Array(0);
    }

    // 5. 第三步：计算光照强度（使用归一化的 density 计算梯度）
    const intensities = new Float32Array(width * height);
    let I_min = Infinity;

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const idx = y * width + x;
            if (dScores[x][y] === 0) {
                intensities[idx] = 0;
                continue;
            }

            // 使用直方图均衡化后的 messiness 值（仅用于光照强度缩放）
            const m = mScores[x][y];
            let m_norm = 0;
            if (USE_HISTOGRAM_EQUALIZATION && sortedM.length > 0) {
                // 使用 Rank 映射（直方图均衡化）
                m_norm = getRankQuantile(m, sortedM);
            } else {
                // 线性映射 fallback
                m_norm = (maxM > 0) ? (m / maxM) : 0;
            }

            // 计算梯度（使用归一化的 density 矩阵，而非原始值）
            const { gx, gy } = computeSobelGradient(dScoresNormalized, x, y);

            // 构造并归一化法向量（内联）
            const nx = -gx;
            const ny = -gy;
            const nz = 1;
            const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
            const normX = nx / nLen;
            const normY = ny / nLen;
            const normZ = nz / nLen;

            // 计算光照（点积，内联）并乘以 messiness 值进行拉伸
            const I_base = Math.max(0, normX * lightDirX + normY * lightDirY + normZ * lightDirZ);
            const I = I_base * m_norm; // 关键：用 messiness 值调制光照强度
            intensities[idx] = I;
            I_min = Math.min(I_min, I);
        }
    }

    // 防止除零
    if (I_min >= I_empty) {
        console.warn('Invalid lighting range');
        I_min = I_empty * 0.5;
    }

    // 6. 第四步：生成像素（使用已归一化的 Density 和光照强度）
    const I_range = I_empty - I_min;
    const pixels = new Uint8Array(width * height * 4);

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const idx = (y * width + x) * 4;
            const d = dScores[x][y];

            if (d === 0) {
                pixels[idx + 3] = 0; // 透明
                continue;
            }

            // 使用已归一化的密度值
            const d_norm = dScoresNormalized[x][y];

            // 从 Density 获取基础颜色
            const [r_base, g_base, b_base] = getMagmaColor(d_norm);
            const [L_base, a, b] = rgbToLab(r_base, g_base, b_base);

            // 计算光照调制量（注意 φ 是负数）
            const I = intensities[y * width + x];
            const I_prime = shadingMagnitude * (I_empty - I) / I_range;

            // 应用光照到 L 通道
            const L_final = Math.max(0, Math.min(100, L_base + I_prime));

            // 转回 RGB
            const [r, g, b_rgb] = labToRgb(L_final, a, b);

            pixels[idx] = r;
            pixels[idx + 1] = g;
            pixels[idx + 2] = b_rgb;
            pixels[idx + 3] = 255;
        }
    }

    const endTime = performance.now();
    console.log(`✨ Illuminated heatmap generated in ${(endTime - startTime).toFixed(1)}ms (${width}x${height})`);

    return pixels;
}

// -----------------------------------------------------------------------------
// 主热图生成函数
// -----------------------------------------------------------------------------

export function createHeatmapPixels(
    scanResult: ScanResult,
    width: number,
    height: number,
    mode: HeatmapMode,
    // 新增参数（用于 IlluminatedMessiness 模式）
    shadingMagnitude?: number,
    lightTheta?: number,
    lightPhi?: number,
    useHistogramEqualization: boolean = true,
    normalizationOptions: HeatmapNormalizationOptions = {}
): Uint8Array {

    // 处理 IlluminatedMessiness 模式
    if (mode === "IlluminatedMessiness") {
        return createIlluminatedMessinessHeatmap(
            scanResult,
            width,
            height,
            shadingMagnitude,
            lightTheta,
            lightPhi,
            normalizationOptions
        );
    }

    // 支持两种结果类型：
    // TdaScanResult: { mScores, dScores }
    // StructureTensorScanResult: { coherenceScores, densityScores }
    let mScores: number[][];
    let dScores: number[][];

    if ('mScores' in scanResult) {
        // TdaScanResult
        mScores = scanResult.mScores;
        dScores = scanResult.dScores;
    } else {
        // StructureTensorScanResult
        // coherenceScores 是一致性得分 (0=无序, 1=有序)
        // 转换为 messiness: messiness = 1 - coherence
        // 但注意：StructureTensorScanResult 中 coherenceScores 已经是 (1 - consistency)
        // 所以直接使用
        mScores = scanResult.coherenceScores;
        dScores = scanResult.densityScores;
    }


    // 预计算 Saliency Matrix (S = M * D)
    const sScores = mScores.map((row, x) =>
        row.map((m, y) => {
            const d = dScores[x]?.[y] || 0;
            return d < 5 ? 0 : m * d; // 阈值过滤，防止噪点
        })
    );

    const pixelData = new Uint8Array(width * height * 4);

    if (mScores.length === 0 || dScores.length === 0 || mode === "Off") {
        return pixelData;
    }

    // =========================================================
    // Pass 1: 数据收集 (用于归一化 或 直方图均衡化)
    // =========================================================

    const densityReference = normalizationOptions.densityReference ?? scanResult;
    const referenceDensityScores = getDensityScoreMatrix(densityReference);
    const { max: maxD, maxLog: maxLogD } = computeDensityScaleStats(
        referenceDensityScores,
        width,
        height
    );

    // 用于直方图均衡化的数组 (仅收集非零值)
    // 使用 Float32Array 减少内存压力，假设最大像素数不超过 2000x2000
    const validMValues: number[] = [];
    const validSValues: number[] = [];

    // 单次遍历获取 Max 值和收集数据
    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const d = dScores[x]?.[y] || 0;
            const m = mScores[x]?.[y] || 0;
            const s = sScores[x]?.[y] || 0;

            // 收集有效值 (忽略背景 d=0 的像素，否则会偏斜 CDF / min-max 范围)
            // 注意：无论是否启用直方图均衡化都需要收集，
            // 因为 min-max 归一化同样依赖这些值来计算 minM/maxM。
            if (d > 0) {
                if (m > 0) validMValues.push(m);
                if (s > 0) validSValues.push(s);
            }
        }
    }

    // =========================================================
    // Sorting: 构建 CDF (如果开启均衡化)
    // =========================================================
    let sortedM: Float32Array = new Float32Array(0);
    let sortedS: Float32Array = new Float32Array(0);
    let minM = Infinity, maxM = -Infinity;
    let minS = Infinity, maxS = -Infinity;

    if (useHistogramEqualization) {
        // 排序，构建查找表
        sortedM = Float32Array.from(validMValues.sort((a, b) => a - b));
        sortedS = Float32Array.from(validSValues.sort((a, b) => a - b));
    } else {
        // 如果不开启均衡化，需要找到 min 和 max 进行 min-max 归一化
        for (let v of validMValues) {
            if (v < minM) minM = v;
            if (v > maxM) maxM = v;
        }
        for (let v of validSValues) {
            if (v < minS) minS = v;
            if (v > maxS) maxS = v;
        }
        // 如果没有有效值，设置默认范围
        if (!isFinite(minM)) minM = 0;
        if (!isFinite(maxM)) maxM = 1;
        if (!isFinite(minS)) minS = 0;
        if (!isFinite(maxS)) maxS = 1;
    }

    // =========================================================
    // Pass 2: 像素生成
    // =========================================================

    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            const pixelIndex = (y * width + x) * 4;

            const d = dScores[x]?.[y] || 0;
            const m = mScores[x]?.[y] || 0;
            const s = sScores[x]?.[y] || 0;

            // 1. 计算 D_norm (密度通常保留 Log 映射，不建议均衡化，保留物理意义)
            let d_norm = 0;
            if (LOG_SCALE_DENSITY) {
                d_norm = (maxLogD > 0) ? (Math.log1p(d) / maxLogD) : 0;
            } else {
                d_norm = (maxD > 0) ? (d / maxD) : 0;
            }

            // 2. 计算 M_norm (杂乱度)
            let m_norm = 0;
            if (useHistogramEqualization) {
                // [核心修改] 使用 Rank 映射
                // 即使 m 集中在 0.9 ~ 1.0，Rank 也会将其拉伸到 0.0 ~ 1.0
                m_norm = (d > 0) ? getRankQuantile(m, sortedM) : 0;
            } else if (d > 0) {
                // 关闭直方图均衡化时，使用有效 messiness 值的 min-max 拉伸到 [0, 1]
                // 若有效值退化为常量场，则保持可见性而不是全部压成 0。
                m_norm = normalizeToUnitInterval(m, minM, maxM, 1);
            }

            // 3. 计算 S_norm (显著性)
            let s_norm = 0;
            if (useHistogramEqualization) {
                s_norm = (d > 0) ? m_norm * d_norm : 0;
            } else {
                // Min-Max 归一化
                const sRange = maxS - minS;
                s_norm = (d > 0 && sRange > 0) ? ((s - minS) / sRange) : 0;
            }

            // 4. 获取颜色
            const [r, g, b, a] = getHeatmapColor(m_norm, d_norm, s_norm, mode);

            pixelData[pixelIndex] = r;
            pixelData[pixelIndex + 1] = g;
            pixelData[pixelIndex + 2] = b;
            pixelData[pixelIndex + 3] = a;
        }
    }

    return pixelData;
}
